import styled from '@emotion/styled';
import { COLORS } from '../../../theme/color';
import { breakpoints } from '../../../theme/theme';

type GoodsProps = {
  $isLiked?: boolean;
  $isOutOfStock?: boolean;
};

type IAtr = {
  $selected: boolean;
};

export const GoodsCon = styled('div') <GoodsProps>`
  background-color: #ffffff;
  border-radius: 0.5rem;
  width: 100%;
  position: relative;
  cursor: ${props => props.$isOutOfStock ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.03);
  overflow: hidden;
  opacity: ${props => props.$isOutOfStock ? 0.6 : 1};
  pointer-events: ${props => props.$isOutOfStock ? 'none' : 'auto'};

  /* Allow like button to still be clickable even when out of stock */
  .like-button {
    pointer-events: auto;
  }

  .imageCon {
    width: 100%;
    height: 7rem; /* Much smaller than original 12rem */
    overflow: hidden;
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
    position: relative;

    img {
      object-fit: contain;
      width: 100%;
      height: 100%;
      padding: 0.5rem; /* Reduced padding */
      transition: transform 0.2s ease-in-out;
    }
  }

  .goodsDetails {
    padding: 0.5rem; /* Reduced padding */
    background-color: #ffffff;

    /* Quantity info styles */
    .quantity-info {
      margin: 0.25rem 0;
      min-height: 24px; /* Always reserve space - this is the key! */
      display: flex;
      align-items: flex-start;
      
      .quantity-badge {
        font-size: 0.625rem;
        padding: 0.125rem 0.5rem;
        border-radius: 0.75rem;
        font-weight: 500;
        display: inline-block;
        
        &.in-stock {
          background-color: #dcfce7;
          color: #166534;
        }
        
        &.low-stock {
          background-color: #fef3c7;
          color: #92400e;
        }
        
        &.out-of-stock {
          background-color: #fecaca;
          color: #991b1b;
        }
      }
    }

    .info {
      &:first-of-type {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        margin-bottom: 0.5rem;
      }

      &:last-child {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0;
        gap: 0.5rem;
      }

      .addScale {
        transform: scale(1);
        transition: transform 0.2s ease-in-out;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px; /* Smaller */
        height: 28px;
        color: white;
        background: ${COLORS.SecondaryBlue};
        border-radius: 4px;

        svg {
          width: 14px; /* Smaller */
          height: 14px;
          fill: white;
        }

        &:hover {
          transform: scale(1.05);
        }
      }
    }

    h3 {
      font-weight: 600;
      font-size: 0.75rem; /* Smaller text */
      line-height: 1.2;
      color: ${COLORS.MainBlack};
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      margin: 0;
      width: 100%;
      height: 2rem; /* Fixed height for consistency */
    }

    h4 {
      font-weight: 700;
      font-size: 0.875rem; /* Smaller price */
      color: ${COLORS.MainGreen};
      white-space: nowrap;
      margin: 0;
      margin-top: 0.125rem;
    }

    p {
      font-size: 0.625rem; /* Much smaller description */
      color: #888;
      line-height: 1.3;
      margin: 0;
      flex: 1;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    /* Bottom actions row */
    .bottom-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 0.25rem;

      .like-button {
        width: 24px;
        height: 24px;
        background: rgba(0, 0, 0, 0.03);
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        transition: all 0.2s ease-in-out;
        cursor: pointer;
        border: none;
        outline: none;

        svg {
          width: 12px;
          height: 12px;
          fill: ${props => (props.$isLiked ? COLORS.MainRed : 'none')};
          path {
            stroke: ${props => (props.$isLiked ? COLORS.MainRed : '#999')};
            stroke-width: 1.5;
          }
        }

        &:hover {
          background: rgba(0, 0, 0, 0.08);
          transform: scale(1.1);
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }
    }
  }

  &:hover {
    transform: ${props => props.$isOutOfStock ? 'none' : 'translateY(-1px)'};
    box-shadow: ${props => props.$isOutOfStock ? '0 1px 3px rgba(0, 0, 0, 0.04)' : '0 4px 12px rgba(0, 0, 0, 0.08)'};

    .imageCon img {
      transform: ${props => props.$isOutOfStock ? 'none' : 'scale(1.02)'};
    }
  }

  @media (min-width: ${breakpoints.sm}px) {
    border-radius: 0.75rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    .imageCon {
      height: 9rem; /* Increased for tablet/desktop but still compact */
      border-top-left-radius: 0.75rem;
      border-top-right-radius: 0.75rem;

      img {
        padding: 0.75rem;
      }
    }

    .goodsDetails {
      padding: 0.75rem;

      .quantity-info {
        margin: 0.375rem 0;
        
        .quantity-badge {
          font-size: 0.75rem;
          padding: 0.25rem 0.75rem;
        }
      }


      .info {
        &:first-of-type {
          gap: 0.375rem;
          margin-bottom: 0.75rem;
        }

        .addScale {
          width: 32px;
          height: 32px;
          border-radius: 6px;

          svg {
            width: 16px;
            height: 16px;
          }
        }
      }

      h3 {
        font-size: 0.875rem;
        height: 2.5rem; /* Slightly more height for larger screens */
      }

      h4 {
        font-size: 1rem;
        margin-top: 0.25rem;
      }

      p {
        font-size: 0.75rem;
        -webkit-line-clamp: 2; /* Allow 2 lines on larger screens */
      }

      .bottom-actions {
        .like-button {
          width: 28px;
          height: 28px;

          svg {
            width: 14px;
            height: 14px;
          }
        }
      }
    }

    &:hover {
      transform: ${props => props.$isOutOfStock ? 'none' : 'translateY(-2px)'};
      box-shadow: ${props => props.$isOutOfStock ? '0 2px 8px rgba(0, 0, 0, 0.06)' : '0 8px 20px rgba(0, 0, 0, 0.12)'};
    }
  }

  /* Desktop - slightly larger but still compact */
  @media (min-width: 1024px) {
    .imageCon {
      height: 10rem; /* Max height for desktop */
    }

    .goodsDetails {
      .quantity-info {
        .quantity-badge {
          font-size: 0.875rem;
          padding: 0.375rem 1rem;
        }
      }
    }
  }
`;

export const GoodsGridGoodsGridCon = styled('div')({
  '.top': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  h4: {
    fontWeight: 700,
    fontSize: '1.25rem'
  },
  p: {
    fontWeight: 300,
    fontSize: '1.1rem',
    textDecoration: 'underline',
    cursor: 'pointer',
    color: COLORS.MainBlue
  },
  a: {
    color: COLORS.MainBlue
  }
});

export const GoodSDetails = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;

  @media (max-width: ${breakpoints.sm}px) {
    grid-template-columns: 1fr;
  }

  .right {
    .imageCon {
      width: 100%;
      height: 18rem;
      overflow: hidden;
      background-color: ${COLORS.MainGray};
      border-radius: 1rem;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        object-fit: contain;
        width: 100%;
        height: 100%;
      }
    }

    .imageGal {
      display: flex;
      justify-content: flex-start;
      flex-wrap: wrap;
      gap: 1rem;

      .imageConGal {
        width: 4.6rem;
        height: 4.6rem;
        overflow: hidden;
        border-radius: 1rem;
        background-color: ${COLORS.MildGray};
        cursor: pointer;
        transition: 0.4s ease-in-out;
        transition: -webkit-transform 0.4s ease-in-out;

        img {
          object-fit: cover;
          width: 100%;
          height: 100%;
        }
        &:hover {
          transform: scale(1.1);
        }
      }
    }
  }

  .left {
    .label {
      font-size: 1rem;
      color: #000000;
      font-weight: 400;

      span {
        font-weight: 500;
      }
    }

    h4 {
      font-weight: 700;
      font-size: 1.5rem;
      color: ${COLORS.MainGreen};
    }

    h1 {
      font-weight: 600;
      font-size: 2rem;
      color: ${COLORS.MainBlack};
    }

    h3 {
      font-weight: 400;
      font-size: 1.125rem;
      color: ${COLORS.SecBlack};
    }
  }
`;

export const AttributeCon = styled('div')`
  p {
    font-size: 1rem;
    color: #000000;
    font-weight: 400;
  }

  .innerCon {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }
`;

export const Atr = styled('div') <IAtr>`
  .color {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 2rem;
    cursor: pointer;
    margin-right: 0.8rem;
    position: relative;

    &:after {
      content: "'";
      display: ${props => (props.$selected ? 'block' : 'none')};
      color: transparent;
      width: 1.9rem;
      height: 1.9rem;
      background-color: transparent;
      border: 0.1rem solid ${COLORS.SecBlack};
      border-radius: 2rem;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
`;

export const btn = {
  maxWidth: '170px',
  padding: '0.7rem'
};

export const AddToCart = styled('div') <GoodsProps>`
  display: flex;
  align-items: center;
  .likeCon {
    height: 2rem;
    width: 2rem;
    border-radius: 1rem;
    background: rgba(13, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: 0.4s ease-in-out;
    transition: -webkit-transform 0.4s ease-in-out;

    svg {
      fill: ${props => (props.$isLiked ? COLORS.MainRed : 'none')};
      path {
        stroke: ${props => (props.$isLiked ? COLORS.MainRed : COLORS.White)};
      }
    }
    &:hover {
      background: rgba(13, 0, 0, 0.7);
      transform: scale(1.05);
    }
  }
`;

export const ProductDsc = styled('div')`
  h3 {
    font-weight: 600;
    font-size: 1.25rem;
  }

  p {
    font-weight: 400;
    font-size: 1rem;
    line-height: 2.25rem;
    color: ${COLORS.SecBlack};
  }
`;

export const RelatedProducts = styled('div')`
  h3 {
    font-weight: 600;
    font-size: 1.25rem;
  }
`;

export const DropButton = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.3rem;
  padding: 0.8em 0.8rem;
  border-radius: 33px;
  font-size: 14px;
  font-weight: 400;
  text-transform: capitalize;
  background: #efefef;
  @media (max-width: ${breakpoints.sm}px) {
    font-size: 13px;
  }
`;

export const List = styled('button')`
  display: block;
  padding: 13px 24px 13px 16px;
  background: none;
  &:hover {
    background: ${COLORS.SecBlue};
    color: ${COLORS.MainBlue};
  }
  text-align: left;
  font-size: 14.5px;
  outline: none;
  border: none;
  @media (max-width: ${breakpoints.sm}px) {
    font-size: 13.8px;
  }
`;

export const BoardContainer = styled('div')`
  position: relative;
  height: 40px;

  div {
    font-weight: 700 !important;
  }

  .board {
    position: absolute;
    left: 0;
    top: 30px;
    z-index: 1;
    width: 850px;
    max-width: calc(100vw - 400px);
    min-height: 426px;
    background: #ffffff;
    border-radius: 5px;
    box-shadow: 0 2px 4px rgb(0 0 0 / 0.1);
    padding: 30px 40px;
    display: flex;

    ul {
      list-style: none;
      font-weight: 300 !important;
      margin-right: 120px;
      li {
        font-size: 14px;
        margin-bottom: 16px;
        color: #999999;
        cursor: pointer;
      }
      li:hover {
        color: ${COLORS.MainBlack};
        scale: 1.01;
      }
      li.header {
        font-size: 16px;
        margin-bottom: 20px;
        font-weight: 500 !important;
        color: ${COLORS.MainBlack};
      }
    }
    ul:last-child {
      margin-right: 0px;
    }
    @media (max-width: 1050px) {
      ul {
        margin-right: 80px;
      }
    }
  }
`;

export const extraModalContentStyle = {
  borderRadius: '12px'
};

export const ProductSkeletonCon = styled('div')`
  .info {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;
